import { useEffect, useMemo, useState } from 'react';
import { useTable } from 'spacetimedb/react';
import { tables } from '@vidmakr/bindings';
import type { Space } from '@vidmakr/bindings/types';
import { Folder, Globe2, Lock, Mail, Trash2, Users } from 'lucide-react';
import { uuidv7 } from '@vidmakr/shared';
import { Avatar, Badge, Button, Field, Input, Modal, Select, Toggle, cx } from '@/components/ui';
import { reducers } from '@/lib/stdb';
import { attempt } from '@/lib/toast';
import { GENERAL, SPACE_COLORS, placeScope, useSpaces, type TargetKind } from '@/lib/spaces';

/** Creates or edits a space. */
export function SpaceDialog({ orgId, space, open, onClose, onCreated }: { orgId: string; space?: Space | null; open: boolean; onClose: () => void; onCreated?: (id: string) => void }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState(SPACE_COLORS[0]);
  const [restricted, setRestricted] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setName(space?.name ?? '');
    setDescription(space?.description ?? '');
    setColor(space?.color ?? SPACE_COLORS[0]);
    setRestricted(false);
  }, [open, space]);
  const save = async () => {
    setBusy(true);
    if (space) {
      if (await attempt(() => reducers().updateSpace({ id: space.id, name, description, color }), 'Space updated')) onClose();
    } else {
      const id = uuidv7();
      if (await attempt(() => reducers().createSpace({ id, orgId, name, description, color, restricted }), 'Space created')) {
        onClose();
        onCreated?.(id);
      }
    }
    setBusy(false);
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={space ? 'Edit space' : 'New space'}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={busy} disabled={!name.trim()} onClick={() => void save()} data-save-space>
            {space ? 'Save' : 'Create space'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Name">
          <Input value={name} autoFocus maxLength={80} placeholder="e.g. Client: Acme, Marketing, Internal" onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && name.trim() && void save()} data-space-name />
        </Field>
        <Field label="Description (optional)">
          <Input value={description} maxLength={500} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <Field label="Color">
          <div className="flex gap-2">
            {SPACE_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                aria-label={c}
                className={cx('h-7 w-7 rounded-full ring-offset-2 ring-offset-panel', color === c && 'ring-2 ring-fg')}
                style={{ background: c }}
                onClick={() => setColor(c)}
              />
            ))}
          </div>
        </Field>
        {!space ? (
          <Toggle
            checked={restricted}
            onChange={setRestricted}
            label={<span className="text-sm">Only people I add can see it (owners and admins always can)</span>}
          />
        ) : null}
      </div>
    </Modal>
  );
}

/** Asks for a name (new folder, rename). */
export function NameDialog({ open, title, initial = '', action, onClose, onSave }: { open: boolean; title: string; initial?: string; action: string; onClose: () => void; onSave: (name: string) => Promise<boolean> }) {
  const [name, setName] = useState(initial);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setName(initial);
  }, [open, initial]);
  const save = async () => {
    if (!name.trim()) return;
    setBusy(true);
    if (await onSave(name.trim())) onClose();
    setBusy(false);
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      width={420}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={busy} disabled={!name.trim()} onClick={() => void save()} data-save-name>
            {action}
          </Button>
        </>
      }
    >
      <Input value={name} autoFocus maxLength={100} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void save()} data-name-input />
    </Modal>
  );
}

/** Picks a space and folder to move a project or folder to (only places the viewer may edit). */
export function MoveDialog({
  orgId,
  open,
  title,
  current,
  excludeFolders,
  onClose,
  onMove,
}: {
  orgId: string;
  open: boolean;
  title: string;
  current: { spaceId: string; folderId: string };
  /** Folders being moved: they and their subfolders are no destination. */
  excludeFolders?: string[];
  onClose: () => void;
  onMove: (spaceId: string, folderId: string) => Promise<boolean>;
}) {
  const { spaces, folders, spaceById, folderById, canIn } = useSpaces(orgId);
  const [spaceId, setSpaceId] = useState(current.spaceId);
  const [folderId, setFolderId] = useState(current.folderId);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      setSpaceId(current.spaceId);
      setFolderId(current.folderId);
    }
  }, [open, current.spaceId, current.folderId]);
  // Folders inside the moved one can't be its destination.
  const excluded = useMemo(() => {
    const out = new Set<string>(excludeFolders ?? []);
    for (let grew = true; grew; ) {
      grew = false;
      for (const f of folders) if (!out.has(f.id) && out.has(f.parentId)) (out.add(f.id), (grew = true));
    }
    return out;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folders, excludeFolders?.join(',')]);
  const tree = useMemo(() => {
    const rows: { id: string; name: string; depth: number; restricted: boolean }[] = [];
    const walk = (parent: string, depth: number) => {
      for (const f of folders.filter((x) => x.spaceId === spaceId && x.parentId === parent && !excluded.has(x.id))) {
        rows.push({ id: f.id, name: f.name, depth, restricted: f.restricted });
        walk(f.id, depth + 1);
      }
    };
    walk('', 0);
    return rows;
  }, [folders, spaceId, excluded]);
  const allowed = canIn(placeScope(spaceById, folderById, spaceId, folderId), 'editor');
  const move = async () => {
    setBusy(true);
    if (await onMove(spaceId, folderId)) onClose();
    setBusy(false);
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      width={460}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={busy} disabled={!allowed || (spaceId === current.spaceId && folderId === current.folderId)} onClick={() => void move()} data-move-confirm>
            Move here
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Space">
          <Select
            className="w-full"
            value={spaceId || GENERAL}
            onChange={(e) => {
              setSpaceId(e.target.value === GENERAL ? '' : e.target.value);
              setFolderId('');
            }}
            data-move-space
          >
            <option value={GENERAL}>General</option>
            {spaces.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
                {s.restricted ? ' 🔒' : ''}
              </option>
            ))}
          </Select>
        </Field>
        <div className="max-h-64 overflow-auto rounded-lg border border-line p-1" role="listbox" aria-label="Folder">
          <button className={cx('flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm', !folderId ? 'bg-accent/15 text-fg' : 'text-muted hover:bg-panel-2')} onClick={() => setFolderId('')}>
            <Folder size={14} /> Top of the space
          </button>
          {tree.map((f) => (
            <button
              key={f.id}
              className={cx('flex w-full items-center gap-2 rounded-md py-1.5 pr-2 text-left text-sm', folderId === f.id ? 'bg-accent/15 text-fg' : 'text-muted hover:bg-panel-2')}
              style={{ paddingLeft: 8 + (f.depth + 1) * 16 }}
              onClick={() => setFolderId(f.id)}
              data-move-folder={f.id}
            >
              <Folder size={14} /> {f.name} {f.restricted ? <Lock size={11} className="text-faint" /> : null}
            </button>
          ))}
        </div>
        {!allowed ? <p className="text-xs text-danger">You can't add things there.</p> : null}
      </div>
    </Modal>
  );
}

/**
 * Who can see a space or folder: everyone who sees what it is in, or only the
 * people and groups added here (plus owners and admins). Also invites people
 * from outside the organization as guests.
 */
export function AccessDialog({ orgId, kind, id, name, restricted, open, onClose }: { orgId: string; kind: TargetKind; id: string; name: string; restricted: boolean; open: boolean; onClose: () => void }) {
  const { members, groups, groupMembers, grants, role } = useSpaces(orgId);
  const [invites] = useTable(tables.orgInvite.where((i) => i.orgId.eq(orgId)));
  const mine = grants.filter((g) => g.targetId === id);
  const [pick, setPick] = useState('');
  const [pickRole, setPickRole] = useState('editor');
  const [email, setEmail] = useState('');
  const [emailRole, setEmailRole] = useState('viewer');
  const [busy, setBusy] = useState('');
  const admin = role === 'owner' || role === 'admin';
  const label = (g: { principalKind: string; principalId: string }) =>
    g.principalKind === 'group' ? (groups.find((x) => x.id === g.principalId)?.name ?? 'Group') : (members.find((m) => m.userId === g.principalId)?.name ?? 'Someone');
  const candidates = [
    ...groups.filter((g) => !mine.some((x) => x.principalKind === 'group' && x.principalId === g.id)).map((g) => ({ key: `group:${g.id}`, label: `${g.name} (group · ${groupMembers.filter((m) => m.groupId === g.id).length})` })),
    ...members
      .filter((m) => m.role !== 'owner' && m.role !== 'admin' && !mine.some((x) => x.principalKind === 'user' && x.principalId === m.userId))
      .map((m) => ({ key: `user:${m.userId}`, label: `${m.name || m.email}${m.role === 'guest' ? ' (guest)' : ''}` })),
  ];
  const grant = async (principal: string, r: string) => {
    const [principalKind, principalId] = principal.split(':');
    setBusy(principal);
    await attempt(() => reducers().setGrant({ kind, id, principalKind, principalId, role: r }));
    setBusy('');
  };
  const pending = invites.filter((i) => i.status === 'pending' && i.grantTargetId === id);
  return (
    <Modal open={open} onClose={onClose} title={`Access · ${name}`} width={560}>
      <div className="space-y-4" data-access-dialog={id}>
        <div className="grid gap-2 sm:grid-cols-2">
          {[false, true].map((r) => (
            <button
              key={String(r)}
              className={cx('flex items-start gap-2 rounded-xl border p-3 text-left', restricted === r ? 'border-accent bg-accent/10' : 'border-line hover:bg-panel-2')}
              onClick={() => restricted !== r && void attempt(() => reducers().setRestricted({ kind, id, restricted: r }), r ? 'Now restricted' : 'Open again')}
              data-access-mode={r ? 'restricted' : 'open'}
            >
              {r ? <Lock size={16} className="mt-0.5 text-accent-2" /> : <Globe2 size={16} className="mt-0.5 text-accent-2" />}
              <span>
                <span className="block text-sm font-medium">{r ? 'Restricted' : 'Open'}</span>
                <span className="block text-xs text-muted">
                  {r ? 'Only the people and groups below, and owners and admins.' : kind === 'space' ? 'Everyone in the organization.' : 'Everyone who can see where it is.'}
                </span>
              </span>
            </button>
          ))}
        </div>

        {restricted ? (
          <>
            <div className="rounded-xl border border-line">
              <div className="border-b border-line px-3 py-2 text-xs font-semibold text-muted">People and groups with access</div>
              {mine.length ? (
                <div className="divide-y divide-line">
                  {mine.map((g) => (
                    <div key={g.id} className="flex items-center gap-2 px-3 py-2 text-sm" data-grant={g.principalId}>
                      {g.principalKind === 'group' ? <Users size={16} className="text-muted" /> : <Avatar name={label(g)} size={22} />}
                      <span className="min-w-0 flex-1 truncate">{label(g)}</span>
                      <Select className="h-8 w-28 text-xs" value={g.role} onChange={(e) => void grant(`${g.principalKind}:${g.principalId}`, e.target.value)}>
                        <option value="viewer">Can view</option>
                        <option value="editor">Can edit</option>
                      </Select>
                      <Button size="icon-sm" variant="ghost" title="Remove access" onClick={() => void grant(`${g.principalKind}:${g.principalId}`, '')}>
                        <Trash2 size={13} />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="px-3 py-3 text-xs text-faint">Nobody yet besides owners and admins.</p>
              )}
              <div className="flex flex-wrap items-center gap-2 border-t border-line bg-panel-2 px-3 py-2">
                <Select className="h-8 min-w-0 flex-1 text-xs" value={pick} onChange={(e) => setPick(e.target.value)} data-grant-pick>
                  <option value="">Add a member or group…</option>
                  {candidates.map((c) => (
                    <option key={c.key} value={c.key}>
                      {c.label}
                    </option>
                  ))}
                </Select>
                <Select className="h-8 w-28 text-xs" value={pickRole} onChange={(e) => setPickRole(e.target.value)} data-grant-role>
                  <option value="viewer">Can view</option>
                  <option value="editor">Can edit</option>
                </Select>
                <Button size="sm" variant="primary" disabled={!pick} loading={busy === pick} onClick={() => void grant(pick, pickRole).then(() => setPick(''))} data-grant-add>
                  Add
                </Button>
              </div>
            </div>

            <div className="rounded-xl border border-line p-3">
              <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-muted">
                <Mail size={13} /> Invite someone from outside
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Input className="h-8 min-w-0 flex-1 text-xs" type="email" placeholder="name@company.com" value={email} onChange={(e) => setEmail(e.target.value)} data-guest-email />
                <Select className="h-8 w-28 text-xs" value={emailRole} onChange={(e) => setEmailRole(e.target.value)}>
                  <option value="viewer">Can view</option>
                  <option value="editor">Can edit</option>
                </Select>
                <Button
                  size="sm"
                  disabled={!email.includes('@')}
                  onClick={() =>
                    void attempt(() => reducers().inviteToTarget({ kind, id, email, role: emailRole }), 'Invitation sent').then((ok) => ok && setEmail(''))
                  }
                  data-guest-invite
                >
                  Invite
                </Button>
              </div>
              <p className="mt-2 text-[11px] text-faint">
                Guests see only what is shared with them. Guests who can view are free; guests who can edit use an editor seat.
              </p>
              {pending.length ? (
                <div className="mt-2 space-y-1">
                  {pending.map((i) => (
                    <div key={i.id} className="flex items-center gap-2 text-xs text-muted">
                      <Badge>Invited</Badge>
                      <span className="flex-1 truncate">{i.email}</span>
                      <span>{i.grantRole === 'editor' ? 'can edit' : 'can view'}</span>
                      {admin ? (
                        <button className="hover:text-fg" onClick={() => void attempt(() => reducers().revokeInvite({ inviteId: i.id }))}>
                          Revoke
                        </button>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </>
        ) : (
          <p className="text-xs text-muted">Restrict it to choose exactly who can see it, or to invite people from outside the organization.</p>
        )}
      </div>
    </Modal>
  );
}
